import capitalize from "lodash/capitalize";
import lowerCase from "lodash/lowerCase";
import {plural} from "pluralize";
import {useState} from "react";
import {FaCircleInfo} from "react-icons/fa6";

import {TooltipWrapper} from "@/components/overlay/TooltipWrapper.tsx";
import {renderFormattedQuantity, shortCurrency} from "@/lib/format.tsx";
import {PlanLimit, PlanLimits} from "@/lib/types/accounts.ts";
import {CalculationUnitPrice, Plan} from "@/lib/types/billing.ts";
import {StepInput} from "@/pages/billing/manage-addons/components/StepInput.tsx";
import {PLAN_FIELD_TOOLTIPS, PURCHASE_ITEM_NAMES} from "@/pages/billing/utils.ts";

export interface AddonRowProps {
  limit: PlanLimit;
  field: keyof PlanLimits;
  addedValues: Record<string, number>;
  onChange: (field: string, value: number) => void;
  currentPlan: Plan;
  unitsPrices: CalculationUnitPrice[];
}

export const AddonRow = ({limit, field, addedValues, currentPlan, unitsPrices, onChange}: AddonRowProps) => {
  const [overload, setOverload] = useState(false);
  const nextPlan = {
    [Plan.Starter]: Plan.Essential,
    [Plan.Essential]: Plan.Premium,
    [Plan.Premium]: Plan.Custom,
    [Plan.Custom]: Plan.Custom,
  }[currentPlan];
  const tooltips = PLAN_FIELD_TOOLTIPS[field];
  let tooltip;
  if (field === "limit_pagespeed_checks") {
    tooltip = (tooltips as Record<Plan, string>)[currentPlan];
  } else {
    tooltip = tooltips as string;
  }
  const unitData = unitsPrices.find((unit) => unit.name === field);
  const price = (unitData?.price || 0) * (addedValues[field] || 0);
  return (
    <tr>
      <td className="p-2 px-3">{plural(PURCHASE_ITEM_NAMES[field])}</td>
      <td className="p-2 px-3">
        <TooltipWrapper toolTip={tooltip}>
          <small>
            <FaCircleInfo className="pulse-color-gray-400" />
          </small>
        </TooltipWrapper>
      </td>
      <td className="p-2 px-3 text-center fw-semibold">
        {renderFormattedQuantity(limit.used, 2)} / {renderFormattedQuantity(limit.allowed, 2)}
      </td>
      <td className="p-2 px-3 text-center">
        <StepInput
          value={addedValues[field] || 0}
          step={limit.bundleSize}
          maxValue={limit.limit - limit.allowed}
          onChange={(value) => onChange(field, value)}
          onOverload={setOverload}
        />
      </td>
      <td className="p-2 px-3">
        {overload && (
          <TooltipWrapper
            toolTip={`Unfortunately, you have reached the maximum purchase limit on your current ${capitalize(lowerCase(currentPlan))} subscription plan. To continue to expand the limits on your account, please upgrade to your ${capitalize(lowerCase(nextPlan))} plan, which offers even higher limits and greater features & functionality.`}
          >
            <small>
              <FaCircleInfo className="pulse-color-red-500" />
            </small>
          </TooltipWrapper>
        )}
      </td>
      <td className="p-2 px-3 text-center fw-semibold">{price ? shortCurrency(price) : "-"}</td>
    </tr>
  );
};
